import React, { useEffect, useState } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

interface NotificationToastProps {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
  /** Rendered inside the global ToastProvider stack - skips the fixed positioning wrapper. */
  inline?: boolean;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  message,
  type,
  isVisible,
  onClose,
  duration = 5000,
  inline = false
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300); // Match animation duration
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-success-text" />;
      case 'error':
        return <ExclamationCircleIcon className="h-5 w-5 text-danger-text" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-warning-text" />;
      case 'info':
      default:
        return <InformationCircleIcon className="h-5 w-5 text-info-text" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return 'bg-success-subtle border-green-200 dark:border-green-800/30 text-success-text';
      case 'error':
        return 'bg-danger-subtle border-red-200 dark:border-red-800/30 text-danger-text';
      case 'warning':
        return 'bg-warning-subtle border-yellow-200 dark:border-yellow-800/30 text-yellow-800 dark:text-yellow-300';
      case 'info':
      default:
        return 'bg-info-subtle border-blue-200 dark:border-blue-800/30 text-info-text';
    }
  };

  if (!isVisible) return null;

  const toastBody = (
    <div className={`
      ${getColors()}
      border rounded-md p-4 shadow-lg backdrop-blur-sm
      transform transition-all duration-slow ease-out
      ${isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      min-w-80 max-w-96
    `} role="status">
      <div className="flex items-start space-x-3">
        <div className="shrink-0 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium leading-5">
            {message}
          </p>
        </div>
        <button
          onClick={handleClose}
          aria-label="Dismiss notification"
          className="shrink-0 ml-4 rounded-lg p-1 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  if (inline) return toastBody;

  return (
    <div className="fixed top-4 right-4 z-50">
      {toastBody}
    </div>
  );
};

export default NotificationToast;